import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { SpotlightResponseHeaderComponent } from '../shared/response-header/response-header.component';
import { SpotlightResponseFooterComponent } from '../shared/response-footer/response-footer.component';
import { SpotlightAvatarComponent } from '../shared/avatar/avatar.component';
import { getColumnType, getColumnWidthClass, getTypeBadgeClass } from './result-list.constants';
import {
    TableHeader,
    extractHeaders,
    getListDisplayHeaders,
    getMemberName,
    getMemberPhoto,
    formatCellValue,
    formatCurrency,
    formatDate,
    capitalize
} from './result-list.mappers';
import { getCounterIcon } from '../result-counter/result-counter.constants';

@Component({
    selector: 'spotlight-result-list',
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        SpotlightResponseHeaderComponent,
        SpotlightResponseFooterComponent,
        SpotlightAvatarComponent
    ],
    templateUrl: './result-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpotlightResultListComponent implements OnChanges {
    @Input() title: string = '';
    @Input() description: string = '';
    @Input() data: any[] = [];
    @Input() executionTimeMs: number = 0;
    @Input() pageSize: number = 10;

    @Output() pageChange = new EventEmitter<number>();

    headers: TableHeader[] = [];
    displayHeaders: TableHeader[] = [];
    currentPage: number = 0;
    totalPages: number = 1;
    paginatedData: any[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data']) {
            this.processData();
        }
    }

    private processData(): void {
        this.headers = extractHeaders(this.data);
        this.displayHeaders = getListDisplayHeaders(this.headers);
        this.totalPages = Math.ceil(this.data.length / this.pageSize);
        this.currentPage = 0;
        this.updatePaginatedData();
    }

    private updatePaginatedData(): void {
        const start = this.currentPage * this.pageSize;
        const end = start + this.pageSize;
        this.paginatedData = this.data.slice(start, end);
    }

    getIcon(): string {
        return getCounterIcon(this.title);
    }

    getColumnType(key: string): string {
        return getColumnType(key);
    }

    getColumnWidth(key: string): string {
        return getColumnWidthClass(key);
    }

    getTypeBadge(type: string): string {
        return getTypeBadgeClass(type);
    }

    getMemberName(row: any): string {
        return getMemberName(row);
    }

    getMemberPhoto(row: any): string | null {
        return getMemberPhoto(row);
    }

    formatValue(value: any, key: string): string {
        return formatCellValue(value, key);
    }

    formatCurrency(value: any): string {
        return formatCurrency(value);
    }

    formatDate(value: any): string {
        return formatDate(value);
    }

    capitalize(value: any): string {
        return capitalize(value);
    }

    onPreviousPage(): void {
        if (this.currentPage > 0) {
            this.currentPage--;
            this.updatePaginatedData();
            this.pageChange.emit(this.currentPage);
        }
    }

    onNextPage(): void {
        if (this.currentPage < this.totalPages - 1) {
            this.currentPage++;
            this.updatePaginatedData();
            this.pageChange.emit(this.currentPage);
        }
    }

    get showPagination(): boolean {
        return this.totalPages > 1;
    }

    get hasMoreData(): boolean {
        return this.data.length > this.pageSize;
    }
}
